import React, {memo, useCallback, useEffect, useMemo, useRef} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {Layout} from '../../styles/layout';
import {
    formatProductPickerPrice,
    formatStockQuantity,
    getProductImageUrl,
    hasStockQuantity,
} from './modeStorage';
import * as S from './styles';
import {ProductPickerItem} from './types';

const ROW_HEIGHT_WITH_IMAGE = 68;
const ROW_HEIGHT_PLAIN = 52;

interface CatalogRowProps {
    product: ProductPickerItem;
    checked: boolean;
    showImages: boolean;
    singlePick?: boolean;
    onToggle: (id: number) => void;
    onPick: (product: ProductPickerItem) => void;
}

const CatalogRow = memo(({
    product,
    checked,
    showImages,
    singlePick,
    onToggle,
    onPick,
}: CatalogRowProps) => {
    const imageUrl = showImages ? getProductImageUrl(product) : undefined;

    const handleActivate = useCallback(() => {
        if (singlePick) {
            onPick(product);
            return;
        }
        onToggle(product.id);
    }, [onPick, onToggle, product, singlePick]);

    return (
        <S.CatalogListRow
            role="button"
            tabIndex={0}
            $selected={checked}
            onClick={handleActivate}
            onDoubleClick={() => {
                if (!singlePick) onPick(product);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActivate();
                }
            }}
        >
            <S.CatalogRowLeft>
                {showImages && (
                    <S.CatalogThumb>
                        {imageUrl ? (
                            <img src={imageUrl} alt="" loading="lazy" decoding="async" />
                        ) : (
                            '—'
                        )}
                    </S.CatalogThumb>
                )}
                <S.ResultMain>
                    <S.ResultTitle>
                        {checked ? '✓ ' : ''}{product.name}
                        {product.has_variation ? ' ' : ''}
                        {product.has_variation && <S.Badge>вариации</S.Badge>}
                    </S.ResultTitle>
                    <S.ResultMeta>
                        {[product.part_number, product.brand, product.category]
                            .filter(Boolean)
                            .join(' · ')}
                    </S.ResultMeta>
                </S.ResultMain>
            </S.CatalogRowLeft>
            <Layout vertical hAlign="flex-end" between={6}>
                <S.ResultPrice>{formatProductPickerPrice(product)}</S.ResultPrice>
                {hasStockQuantity(product) && (
                    <S.StockQty>
                        ост. {formatStockQuantity(product)}
                    </S.StockQty>
                )}
                {!singlePick && (
                    <S.TriggerButton
                        type="button"
                        $compact
                        onClick={(e) => {
                            e.stopPropagation();
                            onPick(product);
                        }}
                        style={{minHeight: 28, padding: '0 10px', fontSize: 12}}
                    >
                        +
                    </S.TriggerButton>
                )}
            </Layout>
        </S.CatalogListRow>
    );
});

CatalogRow.displayName = 'CatalogRow';

interface Props {
    products: ProductPickerItem[];
    selectedIds: number[];
    showImages: boolean;
    singlePick?: boolean;
    loading?: boolean;
    onToggle: (id: number) => void;
    onPick: (product: ProductPickerItem) => void;
}

const VirtualCatalogList = ({
    products,
    selectedIds,
    showImages,
    singlePick,
    loading,
    onToggle,
    onPick,
}: Props) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const rowHeight = showImages ? ROW_HEIGHT_WITH_IMAGE : ROW_HEIGHT_PLAIN;
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const estimateSize = useCallback(() => rowHeight, [rowHeight]);

    const virtualizer = useVirtualizer({
        count: products.length,
        getScrollElement: () => parentRef.current,
        estimateSize,
        overscan: 10,
    });

    useEffect(() => {
        virtualizer.measure();
    }, [showImages, virtualizer]);

    if (products.length === 0) {
        return (
            <S.VirtualScrollRoot>
                <S.EmptyState>
                    {loading ? 'Загрузка…' : 'Нет товаров по фильтру'}
                </S.EmptyState>
            </S.VirtualScrollRoot>
        );
    }

    return (
        <S.VirtualScrollRoot ref={parentRef}>
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                    const product = products[virtualRow.index];
                    if (!product) return null;
                    return (
                        <div
                            key={product.id}
                            data-index={virtualRow.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            <CatalogRow
                                product={product}
                                checked={!singlePick && selectedSet.has(product.id)}
                                showImages={showImages}
                                singlePick={singlePick}
                                onToggle={onToggle}
                                onPick={onPick}
                            />
                        </div>
                    );
                })}
            </div>
        </S.VirtualScrollRoot>
    );
};

export default VirtualCatalogList;
